import AntForm, { type AntSchema, useAntForm } from '@9troisquarts/ant-form'
import { Alert, ConfigProvider, Layout, Typography } from 'antd'
import { LeftOutlined } from '@ant-design/icons'
import React, { useState } from 'react'
import { castAttributesFromDefinition, type ModelDefinitionType } from '@9troisquarts/wand'
import axiosClient from '../../../utils/axiosClient'
import { getLocaleUi, qualimetrieWebsiteUrl, type SupportedLocale } from '../../../utils/localeRegistry'
import useI18n from '../../../utils/useI18n'
import TranslationProvider from '../../../utils/TranslationProvider'
import useWindowDimensions from '../../../utils/useWindowDimensions'
import { Eyebrow } from '@/react/front/components/primitives'
// @ts-expect-error
import whiteLogo from '../../../../assets/images/Qualimetrie-white-logo.svg'

const COMPACT_LAYOUT_MAX_WIDTH = 900

const passwordDefinition: ModelDefinitionType = {
  password: 'Password',
  passwordConfirmation: 'Password'
}

interface EditPasswordProps {
  locale: SupportedLocale
}

const EditPassword: React.FC<EditPasswordProps> = ({ locale }) => {
  const { width } = useWindowDimensions()
  const isCompact = width < COMPACT_LAYOUT_MAX_WIDTH
  const localeUi = getLocaleUi(locale)
  const { t } = useI18n()
  const { object, onChange } = useAntForm({})
  const [errors, setErrors] = useState<any | undefined>(undefined)

  const resetToken = new URLSearchParams(window.location.search).get('reset_password_token') ?? undefined

  const EditPasswordSchema: AntSchema = [
    {
      name: 'password',
      label: t('words.password'),
      required: true,
      input: {
        type: 'password',
        inputProps: {
          placeholder: '******',
          autoComplete: 'new-password'
        }
      },
      colProps: { xs: 24, md: 24 }
    },
    {
      name: 'passwordConfirmation',
      label: t('words.password_confirmation'),
      required: true,
      input: {
        type: 'password',
        inputProps: {
          placeholder: '******',
          autoComplete: 'new-password'
        }
      },
      colProps: { xs: 24, md: 24 }
    }
  ]

  const resetPassword = async (customer: Record<string, unknown>): Promise<void> => {
    await axiosClient.put('/customers/password', {
      customer
    }).then((res) => {
      if (res === undefined) {
        window.location.assign('/')
      }
    }).catch(err => {
      setErrors(err.errors)
    })
  }

  const onSubmit = (): void => {
    void resetPassword({
      ...castAttributesFromDefinition(passwordDefinition, object),
      resetPasswordToken: resetToken
    })
  }

  const goToSignIn = (): void => {
    window.location.assign('/customers/sign_in')
  }

  return (
    <ConfigProvider
      locale={localeUi.antd}
      form={{
        validateMessages: {
          required: t('sentences.auth_field_required')
        }
      }}
    >
      <Layout className='home-layout'>
        <div className='home-container'>
          <header className='home-header'>
            <div className='home-logo'>
              <button type='button' className='home-logo-button' onClick={goToSignIn}>
                <img src={whiteLogo} width={203} height={113} alt="Qualimetrie" />
              </button>
            </div>
          </header>

          <div className='home-split'>
            <section className='home-hero'>
              <Eyebrow className='home-eyebrow'>{t('dashboard.shopperSpace')}</Eyebrow>
              <h1>{t('sentences.welcome_to_qualimetrie')}</h1>
              <p className='home-lead'>{t('sentences.wish_to_become_customer')}</p>
              <p>{t('sentences.home_text_block')}</p>
              <a
                className='qualimetrie-link'
                href={qualimetrieWebsiteUrl(locale)}
                target='_blank'
                rel="noreferrer"
              >
                {t('sentences.discover_qualimetrie')}
              </a>
            </section>

            <aside className='home-panel home-panel--open' id='auth-panel'>
              <div className='home-panel-slot'>
                <div className='home-panel-card'>
                  {isCompact && (
                    <a className='auth-drawer-back home-panel-back' href='/customers/sign_in'>
                      <LeftOutlined /> {t('devise.shared.links.back')}
                    </a>
                  )}
                  {errors?.resetPasswordToken && (
                    <div className='home-panel-alert'>
                      <Alert type='error' message={t('devise.passwords.reset_token_already_used')} />
                    </div>
                  )}
                  <div className='home-panel-heading'>
                    <Eyebrow className='home-panel-eyebrow'>{t('sentences.auth_reset_eyebrow')}</Eyebrow>
                    <Typography.Title level={4} className='home-panel-title'>
                      {t('sentences.reset_password')}
                    </Typography.Title>
                  </div>
                  <AntForm
                    schema={EditPasswordSchema}
                    object={object}
                    layout="vertical"
                    errors={errors}
                    onChange={onChange}
                    onSubmit={onSubmit}
                    submitText={t('sentences.reset_password')}
                    className='auth-form'
                    actionsWrapperProps={{
                      className: 'auth-form-actions'
                    }}
                    submitButtonProps={{
                      className: 'primary-btn'
                    }}
                  />
                  <p className='auth-switch'>
                    <button type='button' className='auth-text-link' onClick={goToSignIn}>
                      {t('sentences.back_to_sign_in')}
                    </button>
                  </p>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </Layout>
    </ConfigProvider>
  )
}

export default TranslationProvider(EditPassword)
